import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { Extension } from "@tiptap/core";
import Collaboration from "@tiptap/extension-collaboration";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { relativePositionToAbsolutePosition, ySyncPluginKey, yUndoPluginKey } from "@tiptap/y-tiptap";
import * as Y from "yjs";
import styled from "styled-components";

import { createNoteExtensions } from "components/organisms/note/extensions";
import ReferenceCitationSuggestion from "components/organisms/note/extensions/ReferenceCitationSuggestion";

import NoteCollaborationProvider from "components/organisms/note/collaboration/NoteCollaborationProvider";
import { createNoteReviewAnchor, createNoteSelectionPayload } from "components/organisms/note/selectionPayload";

import { wsNoteDocumentUrl } from "utils/urls";
import { uuidv4 } from "utils/uuid";

const EditorShell = styled.div`
    width: 100%;
    min-height: 100%;
    padding: 20px 0 56px;
    box-sizing: border-box;
    background: var(--color-Base1);

    .note-editor {
        width: min(72%, 820px);
        max-width: 820px;
        min-height: calc(100vh - 168px);
        margin: 0 auto;
        padding: 64px 72px 80px;
        border: 1px solid var(--color-Line);
        background: var(--color-White);
        color: var(--color-Black);
        font-size: 1.5rem;
        line-height: 1.78;
        letter-spacing: 0;
        word-break: keep-all;
        overflow-wrap: break-word;
    }

    .note-editor > *:first-child {
        margin-top: 0;
    }

    .note-editor > *:last-child {
        margin-bottom: 0;
    }

    .note-editor p {
        margin: 0 0 1.1em;
    }

    .note-editor h1,
    .note-editor h2,
    .note-editor h3 {
        color: var(--color-Black);
        font-weight: 700;
        line-height: 1.28;
        letter-spacing: 0;
    }

    .note-editor h1 {
        margin: 0 0 0.72em;
        font-size: 2.8rem;
    }

    .note-editor h2 {
        margin: 1.55em 0 0.52em;
        font-size: 2.25rem;
    }

    .note-editor h3 {
        margin: 1.35em 0 0.45em;
        font-size: 1.85rem;
    }

    .note-editor ul,
    .note-editor ol {
        margin: 0 0 1.2em;
        padding-left: 1.45em;
    }

    .note-editor li {
        margin: 0.18em 0;
        padding-left: 0.1em;
    }

    .note-editor blockquote {
        margin: 1.25em 0;
        padding: 0.15em 0 0.15em 1em;
        border-left: 3px solid var(--color-Outline);
        color: var(--color-SubBlack);
    }

    .note-editor code {
        padding: 0.1em 0.35em;
        border-radius: 3px;
        background: var(--color-Base1);
        font-size: 0.92em;
    }

    .note-editor a {
        color: var(--color-Key);
        text-decoration: underline;
    }

    .note-editor .note-media-node {
        display: block;
        max-width: 100%;
        margin: 1.4em auto;
        position: relative;
        outline: 2px solid transparent;
        outline-offset: 4px;
        transition:
            outline-color 120ms ease,
            box-shadow 120ms ease;
    }

    .note-editor .note-media-node img[data-note-image],
    .note-editor .note-media-node video[data-note-video] {
        display: block;
        width: 100%;
        max-width: 100%;
        height: auto;
        margin: 0;
        border-radius: 4px;
    }

    .note-editor .note-media-node video[data-note-video] {
        background: #111111;
    }

    .note-editor .note-media-node.ProseMirror-selectednode,
    .note-editor .note-media-node.is-selected {
        outline-color: var(--color-Key);
        box-shadow: 0 0 0 6px rgba(15, 143, 111, 0.12);
    }

    .note-editor .note-media-resize-handle {
        position: absolute;
        right: -9px;
        bottom: -9px;
        width: 18px;
        height: 18px;
        padding: 0;
        border: 2px solid var(--color-White);
        border-radius: 50%;
        background: var(--color-Key);
        box-shadow: 0 2px 8px rgba(15, 23, 42, 0.22);
        cursor: nwse-resize;
    }

    .note-editor table {
        width: 100%;
        margin: 1.4em 0;
        border-collapse: collapse;
        table-layout: fixed;
    }

    .note-editor th,
    .note-editor td {
        min-width: 1em;
        padding: 8px 10px;
        border: 1px solid var(--color-Outline);
        vertical-align: top;
    }

    .note-editor th {
        background: var(--color-Base1);
        font-weight: 700;
    }

    .note-editor .selectedCell {
        background: var(--color-QuoteBox);
    }

    .note-editor:focus,
    .note-editor:focus-visible {
        outline: none;
    }

    .note-reference-citation {
        border-radius: 4px;
        padding: 1px 4px;
        background: var(--color-QuoteBox);
        color: var(--color-SubBlack);
        font-size: 0.95em;
    }

    .note-review-mark {
        border-radius: 3px;
        background: rgba(15, 143, 111, 0.14);
        box-shadow: inset 0 -2px 0 rgba(15, 143, 111, 0.28);
        cursor: pointer;
    }

    .note-review-mark[data-active="true"] {
        background: rgba(15, 143, 111, 0.24);
        box-shadow: inset 0 -2px 0 var(--color-Key);
    }

    @media (max-width: 1024px) {
        .note-editor {
            width: min(88%, 760px);
            padding: 48px 44px 64px;
        }
    }

    @media (max-width: 720px) {
        .note-editor {
            width: calc(100% - 24px);
            padding: 32px 24px 48px;
            font-size: 1.4rem;
        }

        .note-editor h1 {
            font-size: 2.3rem;
        }

        .note-editor h2 {
            font-size: 1.95rem;
        }

        .note-editor h3 {
            font-size: 1.65rem;
        }
    }
`;

const materializeDelayMs = 1200;
const noteReviewPluginKey = new PluginKey("note-review-decorations");

function getMediaAssetType(file) {
    const type = file?.type || "";
    if (type.startsWith("image/")) return "image";
    if (type.startsWith("video/")) return "video";
    return "";
}

function buildNoteMediaNode(file, assetType, uploadedAsset = {}) {
    const src = uploadedAsset.src || uploadedAsset.file || uploadedAsset.url || uploadedAsset.file_url || "";
    if (!src) return null;
    if (assetType === "image") {
        return {
            type: "noteImage",
            attrs: {
                assetId: uploadedAsset.id || null,
                src,
                alt: file?.name || "",
            },
        };
    }
    if (assetType === "video") {
        return {
            type: "noteVideo",
            attrs: {
                assetId: uploadedAsset.id || null,
                src,
                provider: "upload",
            },
        };
    }
    return null;
}

function collectAssetIds(contentJson) {
    const assetIds = new Set();
    const visit = (node) => {
        if (!node) return;
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }
        if ((node.type === "noteImage" || node.type === "noteVideo") && node.attrs?.assetId) {
            assetIds.add(String(node.attrs.assetId));
        }
        (node.content || []).forEach(visit);
    };
    visit(contentJson);
    return assetIds;
}

function base64ToUint8Array(value) {
    if (!value || typeof window === "undefined") return null;
    try {
        const binary = window.atob(value);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
        }
        return bytes;
    } catch (_error) {
        return null;
    }
}

function decodeRelativePosition(value) {
    const bytes = base64ToUint8Array(value);
    if (!bytes) return null;
    try {
        return Y.decodeRelativePosition(bytes);
    } catch (_error) {
        return null;
    }
}

function resolveReviewAnchorRange(state, thread) {
    const anchor = thread?.anchor || {};
    const ystate = ySyncPluginKey.getState(state);
    const yDoc = ystate?.doc;
    const yXmlFragment = ystate?.type;
    const mapping = ystate?.binding?.mapping;
    let from = null;
    let to = null;
    if (yDoc && yXmlFragment && mapping && anchor.relative_from && anchor.relative_to) {
        const relativeFrom = decodeRelativePosition(anchor.relative_from);
        const relativeTo = decodeRelativePosition(anchor.relative_to);
        if (relativeFrom && relativeTo) {
            from = relativePositionToAbsolutePosition(yDoc, yXmlFragment, relativeFrom, mapping);
            to = relativePositionToAbsolutePosition(yDoc, yXmlFragment, relativeTo, mapping);
        }
    }
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
        from = Number(anchor.from);
        to = Number(anchor.to);
    }
    if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return null;
    const docSize = state.doc.content.size;
    if (docSize <= 2) return null;
    return {
        from: Math.max(1, Math.min(docSize - 1, Math.min(from, to))),
        to: Math.max(1, Math.min(docSize - 1, Math.max(from, to))),
    };
}

function buildReviewDecorations(state, pluginState) {
    const threads = pluginState?.threads || [];
    const selectedThreadId = pluginState?.selectedThreadId;
    if (!threads.length) return DecorationSet.empty;
    const decorations = [];
    threads
        .filter((thread) => !thread.is_resolved)
        .forEach((thread) => {
            const range = resolveReviewAnchorRange(state, thread);
            if (!range || range.from === range.to) return;
            decorations.push(
                Decoration.inline(range.from, range.to, {
                    class: "note-review-mark",
                    "data-note-review-id": String(thread.id),
                    "data-active": String(thread.id) === String(selectedThreadId) ? "true" : "false",
                }),
            );
        });
    return DecorationSet.create(state.doc, decorations);
}

function createNoteReviewExtension() {
    return Extension.create({
        name: "noteReviewDecorations",
        addProseMirrorPlugins() {
            return [
                new Plugin({
                    key: noteReviewPluginKey,
                    state: {
                        init: () => ({
                            decorations: DecorationSet.empty,
                            selectedThreadId: null,
                            threads: [],
                        }),
                        apply: (transaction, previousState, _oldState, newState) => {
                            const meta = transaction.getMeta(noteReviewPluginKey);
                            if (!meta && !transaction.docChanged) return previousState;
                            const nextState = meta ? { ...previousState, ...meta } : previousState;
                            return {
                                ...nextState,
                                decorations: buildReviewDecorations(newState, nextState),
                            };
                        },
                    },
                    props: {
                        decorations(state) {
                            return noteReviewPluginKey.getState(state)?.decorations || DecorationSet.empty;
                        },
                        handleClick(view, _pos, event) {
                            if (!view.state.selection.empty) return false;
                            const reviewElement = event.target?.closest?.("[data-note-review-id]");
                            if (!reviewElement) return false;
                            const rect = reviewElement.getBoundingClientRect();
                            view.dom.dispatchEvent(
                                new CustomEvent("note-review-click", {
                                    bubbles: true,
                                    detail: {
                                        threadId: Number(reviewElement.dataset.noteReviewId),
                                        position: buildReviewThreadOverlayPosition(rect),
                                    },
                                }),
                            );
                            return false;
                        },
                    },
                }),
            ];
        },
    });
}

function createNoteMediaUploadExtension({ onUploadError, uploadNoteMedia }) {
    return Extension.create({
        name: "noteMediaUpload",

        addCommands() {
            return {
                insertNoteMediaFile:
                    ({ assetType, file, position = null }) =>
                    ({ editor }) => {
                        const nextAssetType = assetType || getMediaAssetType(file);
                        if (!file || !nextAssetType || !uploadNoteMedia) return false;
                        void (async () => {
                            try {
                                const uploadedAsset = await uploadNoteMedia(file, nextAssetType);
                                const mediaNode = buildNoteMediaNode(file, nextAssetType, uploadedAsset);
                                if (!mediaNode) {
                                    throw new Error("uploaded note media has no file URL");
                                }
                                const chain = editor.chain().focus();
                                if (Number.isFinite(position)) {
                                    chain.insertContentAt(position, mediaNode).run();
                                } else {
                                    chain.insertContent(mediaNode).run();
                                }
                            } catch (error) {
                                onUploadError?.(error, nextAssetType);
                            }
                        })();
                        return true;
                    },
            };
        },

        addProseMirrorPlugins() {
            return [
                new Plugin({
                    key: new PluginKey("note-media-upload-paste"),
                    props: {
                        handlePaste: (_view, event) => {
                            const files = Array.from(event?.clipboardData?.files || []).filter((file) =>
                                getMediaAssetType(file),
                            );
                            if (!files.length) return false;
                            event.preventDefault();
                            files.forEach((file) => {
                                this.editor.commands.insertNoteMediaFile({
                                    file,
                                    assetType: getMediaAssetType(file),
                                });
                            });
                            return true;
                        },
                        handleDrop: (view, event) => {
                            const files = Array.from(event?.dataTransfer?.files || []).filter((file) =>
                                getMediaAssetType(file),
                            );
                            if (!files.length) return false;
                            const dropPosition = view.posAtCoords({
                                left: event.clientX,
                                top: event.clientY,
                            })?.pos;
                            event.preventDefault();
                            files.forEach((file) => {
                                this.editor.commands.insertNoteMediaFile({
                                    file,
                                    assetType: getMediaAssetType(file),
                                    position: dropPosition,
                                });
                            });
                            return true;
                        },
                    },
                }),
            ];
        },
    });
}

function getBrokenCitationText(attrs = {}) {
    return attrs.referenceNo ? `[${attrs.referenceNo}]` : "[ref]";
}

function normalizeBrokenCitationNodes(contentJson) {
    let changed = false;
    const normalizeNode = (node) => {
        if (!node) return node;
        if (node.type === "referenceCitation") {
            const attrs = node.attrs || {};
            if (!attrs.occurrenceId || !attrs.referenceId) {
                changed = true;
                return {
                    type: "text",
                    text: getBrokenCitationText(attrs),
                };
            }
            return node;
        }
        if (!Array.isArray(node.content)) return node;
        const nextContent = node.content.map(normalizeNode).filter(Boolean);
        if (nextContent !== node.content) {
            return {
                ...node,
                content: nextContent,
            };
        }
        return node;
    };

    return {
        changed,
        content: normalizeNode(contentJson),
    };
}

function collectCitations(contentJson) {
    const citations = [];
    const visit = (node) => {
        if (!node) return;
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }
        if (node.type === "referenceCitation") {
            const attrs = node.attrs || {};
            if (attrs.occurrenceId && attrs.referenceId) {
                citations.push({
                    occurrence_id: attrs.occurrenceId,
                    reference_id: attrs.referenceId,
                });
            }
        }
        (node.content || []).forEach(visit);
    };
    visit(contentJson);
    return citations;
}

function buildOutlineTree(items) {
    const roots = [];
    const stack = [];
    items.forEach((item) => {
        const nextItem = { ...item, children: [] };
        while (stack.length && stack[stack.length - 1].level >= nextItem.level) {
            stack.pop();
        }
        if (stack.length) {
            stack[stack.length - 1].children.push(nextItem);
        } else {
            roots.push(nextItem);
        }
        stack.push(nextItem);
    });
    return roots;
}

function extractOutlineItems(currentEditor) {
    const items = [];
    currentEditor.state.doc.descendants((node, pos) => {
        if (node.type.name !== "heading") return;
        const text = node.textContent.trim();
        if (!text) return;
        const id = `note-heading-${pos}-${items.length}`;
        items.push({
            id,
            headingIndex: items.length,
            level: Number(node.attrs?.level || 1),
            pos,
            title: text,
        });
    });
    return items;
}

function applyHeadingDomIds(currentEditor, items) {
    items.forEach((item) => {
        const element = getHeadingElement(currentEditor, item);
        if (!(element instanceof HTMLElement)) return;
        element.id = item.id;
        element.setAttribute("data-note-heading-id", item.id);
    });
}

function getEditorView(currentEditor) {
    if (!currentEditor || currentEditor.isDestroyed) return null;
    try {
        return currentEditor.view || null;
    } catch (_error) {
        return null;
    }
}

function getHeadingElement(currentEditor, item) {
    if (!currentEditor || !item) return null;
    const view = getEditorView(currentEditor);
    if (!view) return null;
    try {
        const nodeElement = typeof item.pos === "number" ? view.nodeDOM(item.pos) : null;
        if (nodeElement instanceof HTMLElement) return nodeElement;
        const elementById = view.dom.querySelector(`[data-note-heading-id="${item.id}"]`);
        if (elementById) return elementById;
        return Array.from(view.dom.querySelectorAll("h1, h2, h3"))[item.headingIndex] || null;
    } catch (_error) {
        return null;
    }
}

function getEditorScrollContainer(currentEditor) {
    const view = getEditorView(currentEditor);
    try {
        return view?.dom?.closest(".note-editor-viewport") || null;
    } catch (_error) {
        return null;
    }
}

function getReviewElement(currentEditor, threadId) {
    const view = getEditorView(currentEditor);
    if (!view || threadId == null) return null;
    return (
        Array.from(view.dom.querySelectorAll("[data-note-review-id]")).find(
            (element) => String(element.dataset.noteReviewId) === String(threadId),
        ) || null
    );
}

function buildReviewThreadOverlayPosition(rect) {
    if (!rect) return null;
    const margin = 12;
    const overlayWidth = Math.min(400, window.innerWidth - margin * 2);
    const overlayHeight = Math.min(520, window.innerHeight - margin * 2);
    let left = rect.right + 10;
    if (left + overlayWidth > window.innerWidth - margin) {
        left = rect.left - overlayWidth - 10;
    }
    return {
        top: Math.max(margin, Math.min(window.innerHeight - overlayHeight - margin, rect.top)),
        left: Math.max(margin, Math.min(window.innerWidth - overlayWidth - margin, left)),
    };
}

function scrollReviewThreadIntoView(currentEditor, threadId) {
    let element = getReviewElement(currentEditor, threadId);
    if (!element) return null;
    const scrollContainer = getEditorScrollContainer(currentEditor);
    if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const targetTop = scrollContainer.scrollTop + elementRect.top - containerRect.top - 80;
        scrollContainer.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "auto",
        });
        element = getReviewElement(currentEditor, threadId);
    }
    return buildReviewThreadOverlayPosition(element?.getBoundingClientRect());
}

function isNodeInsideElement(element, node) {
    if (!element || !node) return false;
    const target = node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode;
    return !!target && element.contains(target);
}

function selectionBelongsToElement(element) {
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;
    return isNodeInsideElement(element, selection.anchorNode) && isNodeInsideElement(element, selection.focusNode);
}

function findActiveOutlineId(items, position) {
    let activeId = "";
    items.forEach((item) => {
        if (item.pos <= position) {
            activeId = item.id;
        }
    });
    return activeId;
}

function findActiveOutlineIdFromScroll(currentEditor, items) {
    const scrollContainer = getEditorScrollContainer(currentEditor);
    if (!scrollContainer || !items.length) return "";
    const containerTop = scrollContainer.getBoundingClientRect().top;
    const activationOffset = 24;
    let activeItem = null;
    let activeTop = Number.NEGATIVE_INFINITY;

    items.forEach((item) => {
        const element = getHeadingElement(currentEditor, item);
        if (!element) return;
        const top = element.getBoundingClientRect().top - containerTop;
        if (top <= activationOffset && top > activeTop) {
            activeItem = item;
            activeTop = top;
        }
    });

    if (activeItem) return activeItem.id;
    return items[0]?.id || "";
}

function scrollHeadingToViewportTop(currentEditor, item) {
    const scrollContainer = getEditorScrollContainer(currentEditor);
    if (!scrollContainer) return;
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            const element = getHeadingElement(currentEditor, item);
            if (!element) return;
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const targetTop = scrollContainer.scrollTop + elementRect.top - containerRect.top - 16;
            scrollContainer.scrollTo({
                top: Math.max(0, targetTop),
                behavior: "smooth",
            });
        });
    });
}

function isRectVisibleInContainer(rect, containerRect, margin = 16) {
    if (!rect || !containerRect) return false;
    return rect.top >= containerRect.top + margin && rect.bottom <= containerRect.bottom - margin;
}

function scrollPositionIntoView(currentEditor, position) {
    const view = getEditorView(currentEditor);
    const scrollContainer = getEditorScrollContainer(currentEditor);
    if (!view || !scrollContainer || !Number.isFinite(position)) return;
    window.requestAnimationFrame(() => {
        let targetElement = null;
        try {
            targetElement = view.nodeDOM(position);
        } catch (_error) {
            targetElement = null;
        }
        if (!(targetElement instanceof HTMLElement)) {
            try {
                const coords = view.coordsAtPos(position);
                const containerRect = scrollContainer.getBoundingClientRect();
                if (
                    isRectVisibleInContainer(
                        {
                            top: coords.top,
                            bottom: coords.bottom,
                        },
                        containerRect,
                    )
                ) {
                    return;
                }
                scrollContainer.scrollTo({
                    top: Math.max(0, scrollContainer.scrollTop + coords.top - containerRect.top - 80),
                    behavior: "smooth",
                });
                return;
            } catch (_error) {
                return;
            }
        }
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = targetElement.getBoundingClientRect();
        if (isRectVisibleInContainer(elementRect, containerRect)) {
            return;
        }
        scrollContainer.scrollTo({
            top: Math.max(0, scrollContainer.scrollTop + elementRect.top - containerRect.top - 80),
            behavior: "smooth",
        });
    });
}

function buildSelectionActionMenuPayload(currentEditor, { getCitationLabel } = {}) {
    const selection = currentEditor?.state?.selection;
    if (!selection || selection.empty) return null;
    if (selection instanceof NodeSelection) return null;
    const view = getEditorView(currentEditor);
    if (!view) return null;
    const from = selection.from;
    const to = selection.to;
    let fromCoords = null;
    let toCoords = null;
    let fallbackCoords = null;
    try {
        fromCoords = view.coordsAtPos(from);
        toCoords = view.coordsAtPos(to);
        fallbackCoords = view.coordsAtPos(selection.head);
    } catch (_error) {
        return null;
    }
    const anchorCoords = fromCoords || toCoords || fallbackCoords;
    if (!anchorCoords) return null;

    const selectionPayload = createNoteSelectionPayload(currentEditor, { getCitationLabel });
    if (!selectionPayload) return null;
    const top = Math.min(fromCoords?.top ?? anchorCoords.top, toCoords?.top ?? anchorCoords.top);
    const left =
        fromCoords && toCoords
            ? (Math.min(fromCoords.left, toCoords.left) + Math.max(fromCoords.right, toCoords.right)) / 2
            : (anchorCoords.left + anchorCoords.right) / 2;

    return {
        top,
        left,
        selection: selectionPayload,
    };
}

function NoteEditor(
    {
        note,
        onActiveOutlineChange,
        onConnectionChange,
        onEditorReady,
        onMaterialize,
        onOpenSelectionActionMenu,
        onOutlineChange,
        onPresenceChange,
        onSelectReviewThread,
        onUploadError,
        reviewThreads = [],
        searchReferences,
        selectedReviewThreadId = null,
        spaceUuid,
        uploadNoteMedia,
    },
    ref,
) {
    const content = useMemo(
        () => note?.document?.content_json || { type: "doc", content: [] },
        [note?.document?.content_json],
    );
    const citationLabelRef = useRef(new Map());
    const citationReferenceRef = useRef(new Map());
    const citationLabels = useMemo(() => {
        const labels = new Map();
        (note?.citations || []).forEach((citation) => {
            if (!citation.occurrence_id) return;
            labels.set(String(citation.occurrence_id), citation.inline_citation_text || "");
        });
        return labels;
    }, [note?.citations]);
    citationLabelRef.current = citationLabels;
    const citationReferences = useMemo(() => {
        const references = new Map();
        (note?.citations || []).forEach((citation) => {
            if (!citation.occurrence_id) return;
            references.set(String(citation.occurrence_id), citation);
        });
        return references;
    }, [note?.citations]);
    citationReferenceRef.current = citationReferences;
    const materializeTimerRef = useRef(null);
    const outlineItemsRef = useRef([]);
    const selectionMenuFrameRef = useRef(null);
    const selectionPointerDownRef = useRef(false);
    const localUndoStackRef = useRef([]);
    const seededRef = useRef(false);
    const uploadNoteMediaRef = useRef(uploadNoteMedia);
    const onUploadErrorRef = useRef(onUploadError);
    const [providerLoaded, setProviderLoaded] = useState(false);
    uploadNoteMediaRef.current = uploadNoteMedia;
    onUploadErrorRef.current = onUploadError;
    const baseExtensions = useMemo(
        () =>
            createNoteExtensions({
                getCitationLabel: (occurrenceId) => {
                    if (!occurrenceId) return "";
                    return citationLabelRef.current.get(String(occurrenceId)) || "";
                },
                getCitationReference: (occurrenceId) => {
                    if (!occurrenceId) return null;
                    return citationReferenceRef.current.get(String(occurrenceId)) || null;
                },
            }),
        [],
    );
    const reviewExtension = useMemo(() => createNoteReviewExtension(), []);
    const mediaUploadExtension = useMemo(
        () =>
            createNoteMediaUploadExtension({
                onUploadError: (...args) => onUploadErrorRef.current?.(...args),
                uploadNoteMedia: (...args) => uploadNoteMediaRef.current?.(...args),
            }),
        [],
    );
    const referenceSuggestionExtension = useMemo(
        () =>
            ReferenceCitationSuggestion.configure({
                onInserted: scrollPositionIntoView,
                searchReferences,
            }),
        [searchReferences],
    );
    const provider = useMemo(() => {
        if (!note?.note_no || !spaceUuid) return null;
        return new NoteCollaborationProvider({
            url: wsNoteDocumentUrl(spaceUuid),
            noteNo: note.note_no,
        });
    }, [note?.note_no, spaceUuid]);

    const syncOutline = useCallback(
        (currentEditor, activePosition = null) => {
            if (!currentEditor) {
                outlineItemsRef.current = [];
                onOutlineChange?.([]);
                onActiveOutlineChange?.("");
                return;
            }
            const flatItems = extractOutlineItems(currentEditor);
            outlineItemsRef.current = flatItems;
            onOutlineChange?.(buildOutlineTree(flatItems));
            if (activePosition != null) {
                onActiveOutlineChange?.(findActiveOutlineId(flatItems, activePosition));
            }
            window.requestAnimationFrame(() => {
                applyHeadingDomIds(currentEditor, flatItems);
            });
        },
        [onActiveOutlineChange, onOutlineChange],
    );

    const scheduleSelectionActionMenu = useCallback(
        (currentEditor) => {
            if (selectionMenuFrameRef.current !== null) {
                window.cancelAnimationFrame(selectionMenuFrameRef.current);
            }
            selectionMenuFrameRef.current = window.requestAnimationFrame(() => {
                selectionMenuFrameRef.current = null;
                if (selectionPointerDownRef.current) return;
                onOpenSelectionActionMenu?.(
                    buildSelectionActionMenuPayload(currentEditor, {
                        getCitationLabel: (occurrenceId) => citationLabelRef.current.get(String(occurrenceId)) || "",
                    }),
                );
            });
        },
        [onOpenSelectionActionMenu],
    );

    const scheduleMaterialize = useMemo(
        () =>
            (currentEditor, delay = materializeDelayMs) => {
                if (!provider?.loaded) return;
                if (materializeTimerRef.current) {
                    clearTimeout(materializeTimerRef.current);
                }
                materializeTimerRef.current = setTimeout(() => {
                    if (!provider.currentVersion) return;
                    const normalized = normalizeBrokenCitationNodes(currentEditor.getJSON());
                    if (normalized.changed) {
                        currentEditor.commands.setContent(normalized.content, false);
                    }
                    const contentJson = normalized.content;
                    provider.sendMaterialize({
                        version: provider.currentVersion,
                        content_json: contentJson,
                        html_content: currentEditor.getHTML(),
                        plain_text_content: currentEditor.getText("\n"),
                        citations: collectCitations(contentJson),
                    });
                }, delay);
            },
        [provider],
    );

    const editor = useEditor(
        {
            extensions: [
                StarterKit.configure({
                    undoRedo: false,
                }),
                ...baseExtensions,
                reviewExtension,
                mediaUploadExtension,
                referenceSuggestionExtension,
                Collaboration.configure({
                    document: provider?.document,
                    field: "default",
                }),
            ],
            editorProps: {
                attributes: {
                    class: "note-editor",
                },
            },
            onUpdate: ({ editor: currentEditor }) => {
                scheduleMaterialize(currentEditor);
                syncOutline(currentEditor, currentEditor.state.selection.from);
            },
            onSelectionUpdate: ({ editor: currentEditor }) => {
                const activeId = findActiveOutlineId(outlineItemsRef.current, currentEditor.state.selection.from);
                onActiveOutlineChange?.(activeId);
                if (!selectionPointerDownRef.current) {
                    scheduleSelectionActionMenu(currentEditor);
                }
            },
        },
        [
            baseExtensions,
            mediaUploadExtension,
            note?.id,
            onActiveOutlineChange,
            provider,
            referenceSuggestionExtension,
            reviewExtension,
            scheduleMaterialize,
            scheduleSelectionActionMenu,
            syncOutline,
        ],
    );

    useEffect(() => {
        if (!editor || editor.isDestroyed) return;
        const transaction = editor.state.tr.setMeta(noteReviewPluginKey, {
            selectedThreadId: selectedReviewThreadId,
            threads: reviewThreads,
        });
        editor.view.dispatch(transaction);
    }, [editor, reviewThreads, selectedReviewThreadId]);

    useEffect(() => {
        const view = getEditorView(editor);
        const editorDom = view?.dom;
        if (!editorDom) return undefined;
        const handleReviewClick = (event) => {
            const threadId = event.detail?.threadId;
            if (!threadId) return;
            onSelectReviewThread?.(threadId, event.detail?.position || null);
        };
        editorDom.addEventListener("note-review-click", handleReviewClick);
        return () => {
            editorDom.removeEventListener("note-review-click", handleReviewClick);
        };
    }, [editor, onSelectReviewThread]);

    useEffect(() => {
        const view = getEditorView(editor);
        const editorDom = view?.dom;
        if (!editor || !editorDom) return undefined;

        const handleMouseDown = () => {
            selectionPointerDownRef.current = true;
        };
        const handleMouseUp = () => {
            selectionPointerDownRef.current = false;
            scheduleSelectionActionMenu(editor);
        };
        const handleWindowMouseUp = () => {
            if (!selectionPointerDownRef.current) return;
            selectionPointerDownRef.current = false;
            if (selectionBelongsToElement(editorDom)) {
                scheduleSelectionActionMenu(editor);
            }
        };
        const handleKeyUp = () => {
            scheduleSelectionActionMenu(editor);
        };
        const handleKeyDown = (event) => {
            const isUndoShortcut =
                (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key?.toLowerCase() === "z";
            if (!isUndoShortcut || !localUndoStackRef.current.length) return;
            event.preventDefault();
            const previousContent = localUndoStackRef.current.pop();
            if (!previousContent) return;
            editor.commands.setContent(previousContent, true);
            scheduleMaterialize(editor, 0);
            syncOutline(editor, editor.state.selection.from);
        };

        editorDom.addEventListener("mousedown", handleMouseDown);
        editorDom.addEventListener("mouseup", handleMouseUp);
        editorDom.addEventListener("keydown", handleKeyDown);
        editorDom.addEventListener("keyup", handleKeyUp);
        window.addEventListener("mouseup", handleWindowMouseUp);
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            editorDom.removeEventListener("mousedown", handleMouseDown);
            editorDom.removeEventListener("mouseup", handleMouseUp);
            editorDom.removeEventListener("keydown", handleKeyDown);
            editorDom.removeEventListener("keyup", handleKeyUp);
            window.removeEventListener("mouseup", handleWindowMouseUp);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [editor, scheduleMaterialize, scheduleSelectionActionMenu, syncOutline]);

    useImperativeHandle(
        ref,
        () => ({
            getRevisionPayload: () => {
                if (!provider) return {};
                let contentJson = null;
                let htmlContent = "";
                let plainTextContent = "";
                if (editor && provider.loaded) {
                    const normalized = normalizeBrokenCitationNodes(editor.getJSON());
                    if (normalized.changed) {
                        editor.commands.setContent(normalized.content, false);
                    }
                    contentJson = normalized.content;
                    htmlContent = editor.getHTML();
                    plainTextContent = editor.getText("\n");
                    provider.sendMaterialize({
                        version: provider.currentVersion,
                        content_json: contentJson,
                        html_content: htmlContent,
                        plain_text_content: plainTextContent,
                        citations: collectCitations(contentJson),
                    });
                }
                const payload = {
                    yjs_snapshot: provider.getSnapshotBase64(),
                    document_version: provider.currentVersion,
                };
                if (contentJson) {
                    payload.content_json = contentJson;
                    payload.html_content = htmlContent;
                    payload.plain_text_content = plainTextContent;
                }
                return payload;
            },
            getAssetIds: () => {
                if (!editor) return new Set();
                return collectAssetIds(editor.getJSON());
            },
            restoreRevisionContent: (revision) => {
                if (!editor || !provider?.loaded || !revision?.content_json) return null;
                editor.commands.setContent(revision.content_json, true);
                syncOutline(editor, editor.state.selection.from);
                return {
                    yjs_snapshot: provider.getSnapshotBase64(),
                    document_version: provider.currentVersion,
                    content_json: editor.getJSON(),
                    html_content: editor.getHTML(),
                    plain_text_content: editor.getText("\n"),
                };
            },
            scrollToOutlineItem: (item) => {
                if (!editor || !item?.id) return;
                if (typeof item.pos === "number") {
                    editor
                        .chain()
                        .focus()
                        .setTextSelection(Math.max(0, item.pos + 1))
                        .run();
                    onActiveOutlineChange?.(item.id);
                }
                scrollHeadingToViewportTop(editor, item);
            },
            updateActiveOutlineFromScroll: () => {
                if (!editor) return;
                const activeId = findActiveOutlineIdFromScroll(editor, outlineItemsRef.current);
                onActiveOutlineChange?.(activeId);
            },
            scrollToReviewThread: (threadId) => {
                if (!editor) return null;
                return scrollReviewThreadIntoView(editor, threadId);
            },
            getSelectionActionMenuPayload: () => {
                if (!editor) return null;
                return buildSelectionActionMenuPayload(editor, {
                    getCitationLabel: (occurrenceId) => citationLabelRef.current.get(String(occurrenceId)) || "",
                });
            },
            getReviewAnchorPayload: () => {
                if (!editor) return null;
                const selectionPayload = createNoteSelectionPayload(editor, {
                    getCitationLabel: (occurrenceId) => citationLabelRef.current.get(String(occurrenceId)) || "",
                });
                const anchor = createNoteReviewAnchor(editor, selectionPayload);
                if (!anchor || !selectionPayload) return null;
                return {
                    anchor,
                    selection: selectionPayload,
                };
            },
            focusEditor: () => {
                if (!editor) return false;
                return editor.chain().focus().run();
            },
            replaceSelectionWithContent: ({ content, from, to }) => {
                if (!editor || !Number.isFinite(from) || !Number.isFinite(to)) return false;
                const undoManager = yUndoPluginKey.getState(editor.state)?.undoManager;
                undoManager?.stopCapturing?.();
                localUndoStackRef.current.push(editor.getJSON());
                const applied = editor
                    .chain()
                    .focus()
                    .insertContentAt(
                        {
                            from: Math.min(from, to),
                            to: Math.max(from, to),
                        },
                        content,
                    )
                    .run();
                if (!applied) {
                    localUndoStackRef.current.pop();
                }
                undoManager?.stopCapturing?.();
                return applied;
            },
            insertReferenceCitation: (reference) => {
                if (!editor || !reference?.id) return false;
                const insertPosition = editor.state.selection.to;
                const occurrenceId = uuidv4();
                const inserted = editor
                    .chain()
                    .focus()
                    .insertContentAt(insertPosition, {
                        type: "referenceCitation",
                        attrs: {
                            occurrenceId,
                            referenceId: reference.id,
                            referenceNo: reference.reference_no,
                        },
                    })
                    .setTextSelection(insertPosition + 1)
                    .run();
                if (inserted) {
                    scrollPositionIntoView(editor, insertPosition);
                }
                return inserted;
            },
        }),
        [editor, onActiveOutlineChange, provider, syncOutline],
    );

    useEffect(() => {
        if (!provider) return undefined;
        const handleLoaded = () => setProviderLoaded(true);
        const handleOpen = () => onConnectionChange?.(true);
        const handleClose = () => {
            onConnectionChange?.(false);
            onPresenceChange?.([]);
        };
        const handleParticipants = (event) => {
            onPresenceChange?.(event.detail?.participants || []);
        };
        setProviderLoaded(false);
        seededRef.current = false;
        provider.addEventListener("loaded", handleLoaded);
        provider.addEventListener("open", handleOpen);
        provider.addEventListener("close", handleClose);
        provider.addEventListener("participants", handleParticipants);
        provider.connect();
        return () => {
            provider.removeEventListener("loaded", handleLoaded);
            provider.removeEventListener("open", handleOpen);
            provider.removeEventListener("close", handleClose);
            provider.removeEventListener("participants", handleParticipants);
            onConnectionChange?.(false);
            onPresenceChange?.([]);
            provider.destroy();
        };
    }, [onConnectionChange, onPresenceChange, provider]);

    useEffect(() => {
        if (!provider || !editor) return undefined;
        const handleVersion = () => {
            scheduleMaterialize(editor, 0);
            syncOutline(editor, editor.state.selection.from);
        };
        provider.addEventListener("version", handleVersion);
        return () => provider.removeEventListener("version", handleVersion);
    }, [editor, provider, scheduleMaterialize, syncOutline]);

    useEffect(() => {
        if (!provider) return undefined;
        const handleMaterializeAck = (event) => {
            onMaterialize?.(event.detail || {});
        };
        provider.addEventListener("materialize_ack", handleMaterializeAck);
        return () => provider.removeEventListener("materialize_ack", handleMaterializeAck);
    }, [onMaterialize, provider]);

    useEffect(() => {
        if (!editor || !provider || !providerLoaded || seededRef.current) return;
        if (!provider.isDocumentEmpty() || !content?.content?.length) return;
        seededRef.current = true;
        const normalized = normalizeBrokenCitationNodes(content);
        editor.commands.setContent(normalized.content, false);
        syncOutline(editor, editor.state.selection.from);
    }, [content, editor, provider, providerLoaded, syncOutline]);

    useEffect(() => {
        if (!editor || !providerLoaded) return;
        const normalized = normalizeBrokenCitationNodes(editor.getJSON());
        if (normalized.changed) {
            editor.commands.setContent(normalized.content, false);
            scheduleMaterialize(editor, 0);
        }
        syncOutline(editor, editor.state.selection.from);
    }, [editor, providerLoaded, scheduleMaterialize, syncOutline]);

    useEffect(() => {
        if (!editor) return;
        const view = getEditorView(editor);
        if (!view) return;
        try {
            const citationElements = view.dom.querySelectorAll("[data-note-reference-citation]");
            citationElements.forEach((element) => {
                const occurrenceId = element.getAttribute("data-occurrence-id");
                const label = occurrenceId ? citationLabels.get(String(occurrenceId)) : "";
                if (label) {
                    element.textContent = label;
                }
            });
        } catch (_error) {
            // Tiptap may briefly expose no mounted view while React is committing.
        }
    }, [citationLabels, editor]);

    useEffect(() => {
        onEditorReady?.(editor || null);
        return () => onEditorReady?.(null);
    }, [editor, onEditorReady]);

    useEffect(() => {
        syncOutline(editor, editor?.state.selection.from ?? null);
    }, [editor, syncOutline]);

    useEffect(() => {
        return () => {
            if (materializeTimerRef.current) {
                clearTimeout(materializeTimerRef.current);
            }
            if (selectionMenuFrameRef.current !== null) {
                window.cancelAnimationFrame(selectionMenuFrameRef.current);
            }
        };
    }, []);

    return (
        <EditorShell className="note-print-scope">
            <EditorContent editor={editor} />
        </EditorShell>
    );
}

export default forwardRef(NoteEditor);
